/**
 * Bulk Import Modal v2
 * 5-step wizard for bulk part import with background processing support.
 * 
 * All timestamps displayed in Indian Standard Time (IST - UTC+5:30)
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Upload, FileSpreadsheet, Download, Loader2, CheckCircle2,
  AlertTriangle, ChevronRight, Sparkles, ArrowLeft, ArrowRight,
  Check, Square, CheckSquare, Edit2, Ban, ExternalLink, Copy,
  MinusCircle, Clock, TableProperties, Flag
} from 'lucide-react';
import { toast } from 'sonner';
import { useBulkImport } from '../../contexts/BulkImportContext';
import { VisualTableEditor } from './VisualTableEditor';
import { formatISTDate } from '../../utils/timezone';
import api from '../../services/api';

const STEPS = [
  { id: 1, key: 'upload', title: 'Upload', icon: Upload },
  { id: 2, key: 'classify', title: 'Classify', icon: Sparkles },
  { id: 3, key: 'review', title: 'Review', icon: CheckCircle2 },
  { id: 4, key: 'complete', title: 'Complete', icon: Flag },
];

export const BulkImportModalV2 = ({ isOpen, onClose, onSuccess }) => {
  const {
    activeJobId,
    activeJobData,
    createJob,
    startClassification,
    startCreation,
    updatePart,
    skipPart,
    bulkSelect,
    trustHighConfidence,
    fetchJobDetail,
    minimizeToBackground,
    cancelJob,
  } = useBulkImport();
  
  // Local state
  const [step, setStep] = useState(1);
  const [parsedParts, setParsedParts] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [filterTab, setFilterTab] = useState('all');
  const [editingPart, setEditingPart] = useState(null);
  const [mainGroups, setMainGroups] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  
  const pollingRef = useRef(null);
  
  // Load main groups for manual editing
  useEffect(() => {
    if (isOpen) {
      loadMainGroups();
    }
  }, [isOpen]);
  
  // Poll for job updates during classification
  useEffect(() => {
    if (activeJobData?.status === 'classifying' || activeJobData?.status === 'creating') {
      pollingRef.current = setInterval(async () => {
        if (activeJobId) {
          // Use silent mode during polling to avoid toast spam
          await fetchJobDetail(activeJobId, { silent: true });
        }
      }, 2000); // Increased to 2s for stability
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [activeJobData?.status, activeJobId, fetchJobDetail]);
  
  // Track if we should delay step transition for progress bar completion
  const [delayedStepTransition, setDelayedStepTransition] = useState(false);
  
  // Sync step with job status (4-step flow now: Upload, Classify, Review, Complete)
  useEffect(() => {
    if (activeJobData) {
      const newStatus = activeJobData.status;
      
      // When transitioning from classifying to review, add a brief delay
      // so user can see the progress bar reach 100%
      if (newStatus === 'review' && step === 2 && !delayedStepTransition) {
        setDelayedStepTransition(true);
        // Wait 800ms for progress bar to fill to 100%, then transition
        const timer = setTimeout(() => {
          setStep(3);
          setDelayedStepTransition(false);
        }, 800);
        return () => clearTimeout(timer);
      }
      
      // Normal step sync for other transitions
      switch (newStatus) {
        case 'pending':
        case 'mapping':
        case 'classifying':
          setStep(2); // Classify step
          break;
        case 'review':
        case 'creating':
          if (!delayedStepTransition) {
            setStep(3); // Review step
          }
          break;
        case 'completed':
          setStep(4); // Complete step
          break;
        default:
          break;
      }
    }
  }, [activeJobData?.status, step, delayedStepTransition]);
  
  const loadMainGroups = async () => {
    try {
      const response = await api.get('/api/v1/taxonomy/main-groups');
      setMainGroups(response.data || []);
    } catch (error) {
      console.error('Failed to load main groups:', error);
    }
  };
  
  const loadSubgroups = async (mainGroupCode) => {
    try {
      const response = await api.get(`/api/v1/taxonomy/subgroups/${mainGroupCode}`);
      setSubgroups(response.data || []);
    } catch (error) {
      console.error('Failed to load subgroups:', error);
      setSubgroups([]);
    }
  };
  
  // Handle data from visual table editor
  const handleTableDataChange = useCallback((validParts) => {
    setParsedParts(validParts);
  }, []);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Only reset if no active job OR job is completed and user dismissed it
      if (!activeJobId) {
        setStep(1);
        setParsedParts([]);
        setFilterTab('all');
        setEditingPart(null);
      }
      // Don't reset step if we have an activeJobId - let the status sync handle it
    }
  }, [isOpen, activeJobId]);
  
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/api/v1/bulk/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bulk_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };
  
  // Step 1 -> Step 2: Create job and start classification directly
  const handleStartClassification = async () => {
    if (parsedParts.length === 0) {
      toast.error('No parts to import. Add at least one part.');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const job = await createJob(parsedParts, `Import ${formatISTDate(new Date())}`);
      await startClassification(job.job_id);
      setStep(2); // Now step 2 is Classify
    } catch (error) {
      console.error('Failed to start classification:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Step 3: Create parts
  const handleCreateParts = async () => {
    if (!activeJobId) return;
    
    const selectedCount = activeJobData?.parts?.filter(p => p.selected && p.status === 'classified').length || 0;
    if (selectedCount === 0) {
      toast.error('No parts selected');
      return;
    }
    
    setIsProcessing(true);
    try {
      await startCreation(activeJobId);
    } catch (error) {
      console.error('Failed to start creation:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Get filtered parts for review step
  const getFilteredParts = () => {
    if (!activeJobData?.parts) return [];
    
    switch (filterTab) {
      case 'high':
        return activeJobData.parts.filter(p => p.confidence_level === 'HIGH' && p.status === 'classified');
      case 'review':
        return activeJobData.parts.filter(p => 
          (p.confidence_level === 'MEDIUM' || p.confidence_level === 'LOW') && p.status === 'classified'
        );
      case 'duplicates':
        return activeJobData.parts.filter(p => p.status === 'duplicate');
      case 'errors':
        return activeJobData.parts.filter(p => p.status === 'error');
      default:
        return activeJobData.parts;
    }
  };
  
  // Counts for tabs
  const getCounts = () => {
    if (!activeJobData?.parts) return { all: 0, high: 0, review: 0, duplicates: 0, errors: 0 };
    
    return {
      all: activeJobData.parts.length,
      high: activeJobData.parts.filter(p => p.confidence_level === 'HIGH' && p.status === 'classified').length,
      review: activeJobData.parts.filter(p => 
        (p.confidence_level === 'MEDIUM' || p.confidence_level === 'LOW') && p.status === 'classified'
      ).length,
      duplicates: activeJobData.parts.filter(p => p.status === 'duplicate').length,
      errors: activeJobData.parts.filter(p => p.status === 'error').length,
    };
  };
  
  // Handle part edit
  const handleEditPart = async (partIndex, updates) => {
    if (!activeJobId) return;
    try {
      await updatePart(activeJobId, partIndex, updates);
      setEditingPart(null);
      toast.success('Classification updated');
    } catch (error) {
      console.error('Failed to update part:', error);
    }
  };
  
  // Handle part skip
  const handleSkipPart = async (partIndex) => {
    if (!activeJobId) return;
    await skipPart(activeJobId, partIndex);
  };
  
  // Toggle selection
  const togglePartSelection = async (partIndex) => {
    if (!activeJobId || !activeJobData?.parts) return;
    const part = activeJobData.parts[partIndex];
    await updatePart(activeJobId, partIndex, { selected: !part.selected });
  };
  
  // Select all visible
  const selectAllVisible = async () => {
    if (!activeJobId || !activeJobData?.parts) return;
    const filteredParts = getFilteredParts();
    const selectableIndices = filteredParts
      .filter(p => p.status === 'classified')
      .map(p => p.index);
    
    const allSelected = filteredParts
      .filter(p => p.status === 'classified')
      .every(p => p.selected);
    
    await bulkSelect(activeJobId, selectableIndices, !allSelected);
  };
  
  // Copy HPN
  const copyHPN = (hpn) => {
    navigator.clipboard.writeText(hpn);
    toast.success('HPN copied');
  };
  
  if (!isOpen) return null;
  
  const counts = getCounts();
  const filteredParts = getFilteredParts();
  const selectedCount = activeJobData?.parts?.filter(p => p.selected && p.status === 'classified').length || 0;
  const progress = activeJobData ? activeJobData.progress_percent : 0;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95, y: 100 }}
        className="relative bg-slate-900 rounded-2xl shadow-2xl w-full max-w-5xl mx-4 max-h-[90vh] flex flex-col overflow-hidden border border-slate-800"
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-slate-800/50 px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Bulk Import Parts</h2>
              <p className="text-sm text-slate-400">
                Step {step} of 4 • {STEPS[step - 1]?.title}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {(activeJobData?.status === 'classifying' || activeJobData?.status === 'creating') && (
              <button
                onClick={minimizeToBackground}
                className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm transition-colors"
                data-testid="minimize-btn"
              >
                <MinusCircle className="w-4 h-4" />
                Continue in Background
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              data-testid="close-bulk-modal"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>
        
        {/* Step Progress */}
        <div className="flex-shrink-0 px-6 py-4 bg-slate-800/30 border-b border-slate-800">
          <div className="flex items-center justify-between max-w-xl mx-auto">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const isComplete = step > s.id;
              const isCurrent = step === s.id;
              
              return (
                <React.Fragment key={s.id}>
                  <div className="flex flex-col items-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                      isComplete ? 'bg-emerald-500' :
                      isCurrent ? 'bg-blue-600' :
                      'bg-slate-700'
                    }`}>
                      {isComplete ? (
                        <Check className="w-4 h-4 text-white" />
                      ) : isCurrent && (activeJobData?.status === 'classifying' || activeJobData?.status === 'creating') ? (
                        <Loader2 className="w-4 h-4 text-white animate-spin" />
                      ) : (
                        <Icon className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <span className={`text-xs mt-1.5 font-medium ${
                      isComplete ? 'text-emerald-400' :
                      isCurrent ? 'text-blue-400' :
                      'text-slate-500'
                    }`}>{s.title}</span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 transition-colors ${
                      step > s.id ? 'bg-emerald-500' : 'bg-slate-700'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <AnimatePresence mode="wait">
            {/* Step 1: Upload with Visual Table Editor */}
            {step === 1 && (
              <Step1Upload
                parsedParts={parsedParts}
                onDataChange={handleTableDataChange}
                onDownloadTemplate={handleDownloadTemplate}
              />
            )}
            
            {/* Step 2: AI Classification */}
            {step === 2 && (
              <Step3Classify
                jobData={activeJobData}
                progress={progress}
              />
            )}
            
            {/* Step 3: Review */}
            {step === 3 && (
              <Step4Review
                jobData={activeJobData}
                filterTab={filterTab}
                setFilterTab={setFilterTab}
                counts={counts}
                filteredParts={filteredParts}
                selectedCount={selectedCount}
                onToggleSelection={togglePartSelection}
                onSelectAll={selectAllVisible}
                onTrustHighConfidence={() => trustHighConfidence(activeJobId)}
                onEditPart={setEditingPart}
                onSkipPart={handleSkipPart}
                mainGroups={mainGroups}
                loadSubgroups={loadSubgroups}
                subgroups={subgroups}
                editingPart={editingPart}
                onSaveEdit={handleEditPart}
                onCancelEdit={() => setEditingPart(null)}
              />
            )}
            
            {/* Step 4: Complete */}
            {step === 4 && (
              <Step5Complete
                jobData={activeJobData}
                onCopyHPN={copyHPN}
                jobId={activeJobId}
              />
            )}
          </AnimatePresence>
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 bg-slate-800/30 border-t border-slate-800 flex items-center justify-between">
          <div className="text-sm text-slate-500">
            {step === 1 && parsedParts.length > 0 && `${parsedParts.length} parts ready`}
            {step === 2 && `${activeJobData?.classified_count || 0} of ${activeJobData?.total_parts || 0} classified`}
            {step === 3 && `${selectedCount} parts selected`}
            {step === 4 && `${activeJobData?.created_count || 0} parts created`}
          </div>
          
          <div className="flex items-center gap-3">
            {step === 1 && (
              <>
                <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white">
                  Cancel
                </button>
                <button
                  onClick={handleStartClassification}
                  disabled={parsedParts.length === 0 || isProcessing}
                  className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Start AI Classification
                </button>
              </>
            )}
            
            {step === 2 && activeJobData?.status === 'review' && (
              <button
                onClick={() => setStep(3)}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium transition-colors"
              >
                Review Results
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            
            {step === 2 && activeJobData?.status === 'classifying' && (
              <button
                onClick={() => cancelJob(activeJobId)}
                className="flex items-center gap-2 px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                data-testid="cancel-classification-btn"
              >
                <X className="w-4 h-4" />
                Cancel Import
              </button>
            )}
            
            {step === 3 && activeJobData?.status === 'review' && (
              <>
                <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white">
                  Cancel
                </button>
                <button
                  onClick={handleCreateParts}
                  disabled={selectedCount === 0 || isProcessing}
                  className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Create {selectedCount} Parts
                </button>
              </>
            )}
            
            {step === 4 && (
              <>
                <button
                  onClick={() => {
                    setStep(1);
                    setParsedParts([]);
                  }}
                  className="px-4 py-2 text-slate-400 hover:text-white"
                >
                  Import More
                </button>
                <button
                  onClick={() => {
                    onSuccess?.();
                    onClose();
                  }}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium transition-colors"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ================== Step Components ==================

const Step1Upload = ({ parsedParts, onDataChange, onDownloadTemplate }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    className="space-y-4"
  >
    {/* Header with download template */}
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-white font-medium">Add Parts Data</h3>
        <p className="text-sm text-slate-400 mt-1">
          Use the table editor or paste data from Excel, Google Sheets, or CSV files
        </p>
      </div>
      <button
        onClick={onDownloadTemplate}
        className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-700 transition-colors"
      >
        <Download className="w-4 h-4" />
        Download Template
      </button>
    </div>
    
    {/* Visual Table Editor */}
    <VisualTableEditor onDataChange={onDataChange} />
    
    {/* Help text */}
    <div className="flex items-start gap-3 p-4 bg-slate-800/30 border border-slate-700 rounded-xl text-sm">
      <AlertTriangle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
      <div className="text-slate-400 space-y-1">
        <p><strong className="text-slate-300">Paste from Excel:</strong> Copy cells in Excel and paste directly into the table or paste area.</p>
        <p><strong className="text-slate-300">Tab key:</strong> Press Tab in the paste area to insert tab characters.</p>
        <p><strong className="text-slate-300">Keyboard navigation:</strong> Tab/Enter to move between cells, Arrow keys to navigate.</p>
      </div>
    </div>
  </motion.div>
);

const Step3Classify = ({ jobData, progress }) => {
  // AI thinking messages that rotate during classification
  const AI_THINKING_MESSAGES = [
    { message: "Analyzing part specifications...", icon: "🔍" },
    { message: "Querying manufacturer database...", icon: "🏭" },
    { message: "Cross-referencing product categories...", icon: "📊" },
    { message: "Evaluating technical characteristics...", icon: "⚙️" },
    { message: "Matching against 50,000+ parts...", icon: "🗄️" },
    { message: "Running AI classification model...", icon: "🤖" },
    { message: "Calculating confidence scores...", icon: "📈" },
    { message: "Validating classification results...", icon: "✅" },
  ];

  // Classification stages
  const CLASSIFICATION_STAGES = [
    { id: 'parse', label: 'Reading part data', duration: 1 },
    { id: 'identify', label: 'Identifying manufacturer', duration: 2 },
    { id: 'query', label: 'Querying parts database', duration: 3 },
    { id: 'classify', label: 'Running AI classification', duration: 4 },
    { id: 'validate', label: 'Validating results', duration: 2 },
  ];

  const [thinkingIndex, setThinkingIndex] = useState(0);
  const [currentStage, setCurrentStage] = useState(0);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Determine ACTUAL classification state based on part statuses, not just job status
  // This fixes the issue where job.status might be stale but parts show the real state
  const getActualClassificationState = () => {
    if (!jobData?.parts || jobData.parts.length === 0) {
      return 'initializing';
    }
    
    const partStatuses = jobData.parts.map(p => p.status);
    const hasClassifying = partStatuses.includes('classifying');
    const hasPending = partStatuses.includes('pending');
    const hasQueued = partStatuses.some(s => !s || s === 'pending' || s === 'queued');
    const allClassified = partStatuses.every(s => s === 'classified' || s === 'duplicate' || s === 'error' || s === 'skipped');
    
    // If any part is actively being classified
    if (hasClassifying) {
      return 'classifying';
    }
    
    // If all parts are done (classified, duplicate, error, or skipped)
    if (allClassified && jobData.parts.length > 0) {
      return 'complete';
    }
    
    // If we have pending/queued parts but none classifying yet, we're still initializing
    if (hasQueued || hasPending) {
      return 'initializing';
    }
    
    // Default to job status
    return jobData.status === 'review' ? 'complete' : 'classifying';
  };

  const actualState = getActualClassificationState();
  const isClassifying = actualState === 'classifying' || actualState === 'initializing';

  // Rotate thinking messages - run when classifying OR initializing
  useEffect(() => {
    if (isClassifying) {
      const interval = setInterval(() => {
        setThinkingIndex(prev => (prev + 1) % AI_THINKING_MESSAGES.length);
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [isClassifying]);

  // Progress stage animation - tie to actual classification progress, not just a timer
  useEffect(() => {
    if (isClassifying) {
      // Calculate stage based on actual progress
      const progressPercent = jobData?.total_parts > 0 
        ? ((jobData?.classified_count || 0) + (jobData?.duplicate_count || 0) + (jobData?.error_count || 0)) / jobData.total_parts 
        : 0;
      
      // Map progress to stages (0-20%: stage 0, 20-40%: stage 1, etc.)
      // But also advance stages based on time if stuck at same progress
      const progressStage = Math.min(Math.floor(progressPercent * 5), CLASSIFICATION_STAGES.length - 1);
      
      // Set stage based on progress or time elapsed (whichever is higher)
      const timeBasedStage = Math.min(Math.floor(elapsedTime / 4), CLASSIFICATION_STAGES.length - 1);
      const newStage = Math.max(progressStage, timeBasedStage);
      
      if (newStage !== currentStage) {
        setCurrentStage(newStage);
      }
    }
  }, [isClassifying, jobData?.classified_count, jobData?.duplicate_count, jobData?.error_count, jobData?.total_parts, elapsedTime, currentStage]);

  // Elapsed time counter - START IMMEDIATELY when component mounts (not waiting for status)
  useEffect(() => {
    if (isClassifying) {
      const interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isClassifying]);

  // Smooth progress animation - time-based continuous progress
  useEffect(() => {
    const actualProgress = jobData?.total_parts > 0 
      ? Math.round(((jobData?.classified_count || 0) + (jobData?.duplicate_count || 0) + (jobData?.error_count || 0)) / jobData.total_parts * 100)
      : 0;
    
    // Animate progress based on both actual progress AND elapsed time
    const animate = () => {
      setAnimatedProgress(prev => {
        // If actual progress is 100% (all parts classified), quickly fill to 100%
        if (actualProgress >= 100) {
          if (prev < 100) {
            return Math.min(prev + 5, 100); // Quick fill to 100%
          }
          return 100;
        }
        
        // If actual progress is higher than animated, catch up
        if (actualProgress > prev) {
          return Math.min(prev + 2, actualProgress);
        }
        
        // If still classifying, create smooth time-based progress
        if (isClassifying && prev < 95) {
          // Calculate expected progress based on elapsed time
          // Aim to reach ~95% before completion (leaves room for final jump to 100%)
          const estimatedTotalTime = (jobData?.total_parts || 1) * 4; // 4 seconds per part
          const timeBasedProgress = Math.min((elapsedTime / estimatedTotalTime) * 95, 95);
          
          // Take the higher of current progress or time-based progress
          const targetProgress = Math.max(prev, timeBasedProgress, actualProgress);
          
          // Smooth increment towards target
          if (prev < targetProgress) {
            return Math.min(prev + 0.5, targetProgress);
          }
          
          // If we're at target but still classifying, slow crawl forward (up to 95%)
          if (prev < 95) {
            return prev + 0.1;
          }
        }
        
        return prev;
      });
    };
    
    const interval = setInterval(animate, 100);
    return () => clearInterval(interval);
  }, [jobData?.classified_count, jobData?.duplicate_count, jobData?.error_count, jobData?.total_parts, isClassifying, elapsedTime]);

  // Guard against missing or invalid job data
  if (!jobData || !jobData.parts || jobData.total_parts === undefined) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex flex-col items-center justify-center p-12 space-y-4"
      >
        <Loader2 className="w-10 h-10 animate-spin text-blue-400" />
        <span className="text-slate-400 text-lg">Initializing AI Classification...</span>
        <span className="text-slate-500 text-sm">Setting up the classification engine</span>
      </motion.div>
    );
  }
  
  // Find the part currently being classified OR the first pending part
  const currentlyClassifying = jobData.parts.find(p => p.status === 'classifying') 
    || (isClassifying ? jobData.parts.find(p => !p.status || p.status === 'pending' || p.status === 'queued') : null);
  const completedCount = (jobData.classified_count || 0) + (jobData.duplicate_count || 0) + (jobData.error_count || 0);
  const remainingParts = (jobData.total_parts || 0) - completedCount;
  const estimatedSeconds = Math.max(1, Math.ceil(remainingParts * 3));
  
  // Format time display
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const currentThinking = AI_THINKING_MESSAGES[thinkingIndex];
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Main Progress Card */}
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700 rounded-2xl p-6 overflow-hidden relative">
        {/* Animated background gradient - show when classifying OR initializing */}
        {isClassifying && (
          <motion.div
            className="absolute inset-0 opacity-10"
            animate={{
              background: [
                'radial-gradient(circle at 0% 0%, #3b82f6 0%, transparent 50%)',
                'radial-gradient(circle at 100% 100%, #3b82f6 0%, transparent 50%)',
                'radial-gradient(circle at 0% 100%, #3b82f6 0%, transparent 50%)',
                'radial-gradient(circle at 100% 0%, #3b82f6 0%, transparent 50%)',
              ]
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          />
        )}

        {/* Header */}
        <div className="relative flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isClassifying 
                ? 'bg-blue-500/20 border border-blue-500/30' 
                : 'bg-emerald-500/20 border border-emerald-500/30'
            }`}>
              {isClassifying ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <Sparkles className="w-6 h-6 text-blue-400" />
                </motion.div>
              ) : (
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {isClassifying 
                  ? (actualState === 'initializing' ? 'Starting AI Classification...' : 'AI Classification in Progress')
                  : 'Classification Complete'}
              </h3>
              {isClassifying && currentlyClassifying && (
                <motion.p 
                  key={currentlyClassifying.mpn}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-blue-400"
                >
                  {actualState === 'initializing' ? 'Preparing' : 'Analyzing'}: <span className="font-mono font-medium">{currentlyClassifying.manufacturer} {currentlyClassifying.mpn}</span>
                </motion.p>
              )}
              {!isClassifying && (
                <p className="text-sm text-emerald-400">All parts have been classified successfully</p>
              )}
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-2xl font-bold text-white">{Math.round(animatedProgress)}%</p>
            <p className="text-xs text-slate-500">
              {completedCount} of {jobData.total_parts} parts
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative mb-4">
          <div className="w-full h-4 bg-slate-700/50 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full relative overflow-hidden"
              style={{
                background: isClassifying 
                  ? 'linear-gradient(90deg, #3b82f6, #60a5fa, #3b82f6)'
                  : 'linear-gradient(90deg, #10b981, #34d399)'
              }}
              initial={{ width: 0 }}
              animate={{ width: `${Math.max(animatedProgress, isClassifying ? 3 : 0)}%` }}
              transition={{ duration: 0.3 }}
            >
              {/* Shimmer effect - show when classifying OR initializing */}
              {isClassifying && (
                <motion.div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  }}
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                />
              )}
            </motion.div>
          </div>
        </div>

        {/* Time & Status Row */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-slate-400">
              <Clock className="w-4 h-4" />
              Elapsed: {formatTime(elapsedTime)}
            </span>
            {isClassifying && remainingParts > 0 && (
              <span className="text-slate-500">
                ~{formatTime(estimatedSeconds)} remaining
              </span>
            )}
          </div>
          {!isClassifying && (
            <span className="text-emerald-400 font-medium">Ready for review</span>
          )}
        </div>
      </div>

      {/* AI Thinking Messages Card - show when classifying OR initializing */}
      {isClassifying && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <motion.span
                key={thinkingIndex}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-lg"
              >
                {currentThinking.icon}
              </motion.span>
            </div>
            <motion.p
              key={thinkingIndex}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-slate-300 font-medium"
            >
              {currentThinking.message}
            </motion.p>
          </div>

          {/* Classification Stages */}
          <div className="space-y-2">
            {CLASSIFICATION_STAGES.map((stage, idx) => {
              const isComplete = idx < currentStage;
              const isCurrent = idx === currentStage;
              const isPending = idx > currentStage;

              return (
                <motion.div
                  key={stage.id}
                  initial={{ opacity: 0.5 }}
                  animate={{ opacity: isCurrent ? 1 : isComplete ? 0.7 : 0.4 }}
                  className="flex items-center gap-3"
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isComplete ? 'bg-emerald-500' :
                    isCurrent ? 'bg-blue-500' :
                    'bg-slate-700'
                  }`}>
                    {isComplete ? (
                      <Check className="w-3 h-3 text-white" />
                    ) : isCurrent ? (
                      <motion.div
                        className="w-2 h-2 bg-white rounded-full"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      />
                    ) : (
                      <div className="w-2 h-2 bg-slate-500 rounded-full" />
                    )}
                  </div>
                  <span className={`text-sm ${
                    isComplete ? 'text-emerald-400' :
                    isCurrent ? 'text-white font-medium' :
                    'text-slate-500'
                  }`}>
                    {stage.label}
                    {isCurrent && (
                      <motion.span
                        animate={{ opacity: [1, 0.3, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="ml-2 text-blue-400"
                      >
                        in progress...
                      </motion.span>
                    )}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Parts being processed */}
      <div className="space-y-3 max-h-64 overflow-y-auto">
        <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2">
          <TableProperties className="w-4 h-4" />
          Classification Results
        </h4>
        {jobData?.parts?.slice(0, 15).map((part, i) => (
          <motion.div
            key={part.index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            className={`border rounded-xl p-3 ${
              part.status === 'classified' ? 'border-emerald-500/30 bg-emerald-500/5' :
              part.status === 'duplicate' ? 'border-amber-500/30 bg-amber-500/5' :
              part.status === 'error' ? 'border-red-500/30 bg-red-500/5' :
              part.status === 'classifying' ? 'border-blue-500/50 bg-blue-500/10' :
              'border-slate-700/50 bg-slate-800/30'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  part.status === 'classified' ? 'bg-emerald-500/20' :
                  part.status === 'duplicate' ? 'bg-amber-500/20' :
                  part.status === 'error' ? 'bg-red-500/20' :
                  part.status === 'classifying' ? 'bg-blue-500/20' :
                  isClassifying ? 'bg-blue-500/10' : 'bg-slate-700/50'
                }`}>
                  {part.status === 'classified' ? (
                    <Check className="w-4 h-4 text-emerald-400" />
                  ) : part.status === 'duplicate' ? (
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                  ) : part.status === 'error' ? (
                    <X className="w-4 h-4 text-red-400" />
                  ) : part.status === 'classifying' ? (
                    <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                  ) : isClassifying ? (
                    <Loader2 className="w-4 h-4 text-blue-400/60 animate-spin" />
                  ) : (
                    <Clock className="w-4 h-4 text-slate-500" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium text-sm">{part.manufacturer}</span>
                    <span className="font-mono text-xs text-blue-400">{part.mpn}</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate max-w-md">{part.description}</p>
                </div>
              </div>
              <div className="text-right flex-shrink-0 ml-4">
                {part.status === 'classified' && (
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${
                      part.confidence_level === 'HIGH' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
                      part.confidence_level === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                      'bg-red-500/20 text-red-400 border-red-500/30'
                    }`}>
                      {part.confidence}%
                    </span>
                    <span className="text-xs text-slate-500 hidden sm:inline">
                      {part.main_group_code}
                    </span>
                  </div>
                )}
                {part.status === 'duplicate' && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                    Duplicate
                  </span>
                )}
                {part.status === 'classifying' && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30 animate-pulse">
                    Analyzing...
                  </span>
                )}
                {(!part.status || part.status === 'pending' || part.status === 'queued') && (
                  <span className={`px-2 py-0.5 text-xs rounded-full border ${
                    isClassifying 
                      ? 'bg-blue-500/10 text-blue-400 border-blue-500/30 animate-pulse' 
                      : 'bg-slate-700 text-slate-400 border-slate-600'
                  }`}>
                    {isClassifying ? 'In Queue...' : 'Queued'}
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        ))}
        {jobData.parts.length > 15 && (
          <p className="text-center text-sm text-slate-500 py-2">
            +{jobData.parts.length - 15} more parts
          </p>
        )}
      </div>
    
      {/* Stats Dashboard */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-800/50 rounded-xl p-4 text-center border border-slate-700/50">
          <p className="text-2xl font-bold text-white">{jobData?.total_parts || 0}</p>
          <p className="text-xs text-slate-500 uppercase">Total Parts</p>
        </div>
        <div className="bg-emerald-500/10 rounded-xl p-4 text-center border border-emerald-500/20">
          <p className="text-2xl font-bold text-emerald-400">
            {jobData?.parts?.filter(p => p.confidence_level === 'HIGH').length || 0}
          </p>
          <p className="text-xs text-emerald-500 uppercase">High Confidence</p>
        </div>
        <div className="bg-amber-500/10 rounded-xl p-4 text-center border border-amber-500/20">
          <p className="text-2xl font-bold text-amber-400">
            {jobData?.parts?.filter(p => p.confidence_level === 'MEDIUM' || p.confidence_level === 'LOW').length || 0}
          </p>
          <p className="text-xs text-amber-500 uppercase">Needs Review</p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-4 text-center border border-slate-700/50">
          <p className="text-2xl font-bold text-slate-400">{jobData?.duplicate_count || 0}</p>
          <p className="text-xs text-slate-500 uppercase">Duplicates</p>
        </div>
      </div>
    </motion.div>
  );
};

const Step4Review = ({
  jobData, filterTab, setFilterTab, counts, filteredParts, selectedCount,
  onToggleSelection, onSelectAll, onTrustHighConfidence, onEditPart, onSkipPart,
  mainGroups, loadSubgroups, subgroups, editingPart, onSaveEdit, onCancelEdit
}) => {
  const selectableCount = filteredParts.filter(p => p.status === 'classified').length;
  const allSelected = selectableCount > 0 && filteredParts.filter(p => p.status === 'classified').every(p => p.selected);
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      {/* Filter tabs & actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          {[
            { id: 'all', label: `All (${counts.all})` },
            { id: 'high', label: `High ≥80% (${counts.high})`, color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
            { id: 'review', label: `Review <80% (${counts.review})`, color: 'text-amber-400 border-amber-500/30 bg-amber-500/10' },
            { id: 'duplicates', label: `Duplicates (${counts.duplicates})`, color: 'text-red-400 border-red-500/30 bg-red-500/10' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilterTab(tab.id)}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                filterTab === tab.id
                  ? 'bg-blue-600 text-white border-blue-600'
                  : tab.color || 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onSelectAll}
            className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition-colors"
          >
            {allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
          <button
            onClick={onTrustHighConfidence}
            className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Trust High-Confidence
          </button>
        </div>
      </div>
      
      {/* Results list */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {filteredParts.map(part => (
          <div
            key={part.index}
            className={`border rounded-xl overflow-hidden transition-all ${
              part.status === 'duplicate' ? 'border-red-500/30 bg-red-500/5' :
              part.selected ? 'border-blue-500/50 bg-blue-500/5' :
              'border-slate-700 bg-slate-800/30'
            }`}
          >
            <div className="px-4 py-3 flex items-start gap-3">
              {/* Selection checkbox */}
              {part.status === 'classified' && (
                <button
                  onClick={() => onToggleSelection(part.index)}
                  className="mt-1 flex-shrink-0"
                >
                  {part.selected ? (
                    <CheckSquare className="w-5 h-5 text-blue-400" />
                  ) : (
                    <Square className="w-5 h-5 text-slate-500" />
                  )}
                </button>
              )}
              
              {/* Part info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm text-slate-500">#{part.index + 1}</span>
                  <span className="font-medium text-white">{part.manufacturer}</span>
                  <span className="font-mono text-sm text-blue-400">{part.mpn}</span>
                </div>
                <p className="text-sm text-slate-400 line-clamp-1">{part.description}</p>
                
                {/* Classification */}
                {part.status === 'classified' && (
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <span className="px-2 py-0.5 bg-slate-700 text-white rounded font-mono text-xs">
                      {part.main_group_code}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-600" />
                    <span className="text-slate-300">{part.subgroup_name}</span>
                    {part.subgroup_is_new && (
                      <span className="px-1.5 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded">NEW</span>
                    )}
                  </div>
                )}
                
                {part.rationale && (
                  <p className="mt-1 text-xs text-slate-500 italic line-clamp-1">&ldquo;{part.rationale}&rdquo;</p>
                )}
                
                {part.status === 'duplicate' && part.existing_hpn && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Already exists as <span className="font-mono">{part.existing_hpn}</span>
                  </p>
                )}
              </div>
              
              {/* Confidence & Actions */}
              <div className="flex items-center gap-3 flex-shrink-0">
                {part.status === 'classified' && (
                  <span className={`px-3 py-1 text-sm font-semibold rounded-full border ${
                    part.confidence_level === 'HIGH' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
                    part.confidence_level === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                    'bg-red-500/20 text-red-400 border-red-500/30'
                  }`}>
                    {part.confidence}%
                  </span>
                )}
                
                {part.status === 'classified' && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onEditPart(part)}
                      className={`p-2 rounded-lg transition-colors ${
                        part.confidence_level !== 'HIGH'
                          ? 'bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30'
                          : 'hover:bg-slate-700'
                      }`}
                      title="Edit Classification"
                    >
                      <Edit2 className={`w-4 h-4 ${part.confidence_level !== 'HIGH' ? 'text-amber-400' : 'text-slate-400'}`} />
                    </button>
                    <button
                      onClick={() => onSkipPart(part.index)}
                      className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                      title="Skip Part"
                    >
                      <Ban className="w-4 h-4 text-slate-400 hover:text-red-400" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {filteredParts.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No parts in this category
          </div>
        )}
      </div>
      
      {/* Edit Modal */}
      {editingPart && (
        <EditPartPopover
          part={editingPart}
          mainGroups={mainGroups}
          loadSubgroups={loadSubgroups}
          subgroups={subgroups}
          onSave={onSaveEdit}
          onCancel={onCancelEdit}
        />
      )}
      
      {/* Summary */}
      <div className="flex items-center gap-6 p-4 bg-slate-800/50 rounded-xl text-sm">
        <span className="text-slate-400"><strong className="text-white">{selectedCount}</strong> parts selected</span>
        <span className="text-emerald-400"><strong>{counts.high}</strong> high confidence</span>
        <span className="text-amber-400"><strong>{counts.review}</strong> needs review</span>
        {jobData?.parts?.some(p => p.subgroup_is_new && p.selected) && (
          <span className="text-emerald-400">
            <strong>{jobData.parts.filter(p => p.subgroup_is_new && p.selected).length}</strong> new subgroups
          </span>
        )}
      </div>
    </motion.div>
  );
};

const EditPartPopover = ({ part, mainGroups, loadSubgroups, subgroups, onSave, onCancel }) => {
  const [mainGroup, setMainGroup] = useState(part.main_group_code || '');
  const [subgroupName, setSubgroupName] = useState(part.subgroup_name || '');
  const [isNew, setIsNew] = useState(part.subgroup_is_new || false);
  const [localSubgroups, setLocalSubgroups] = useState(subgroups);
  
  useEffect(() => {
    if (mainGroup) {
      loadSubgroups(mainGroup).then(() => {});
    }
  }, [mainGroup, loadSubgroups]);
  
  useEffect(() => {
    setLocalSubgroups(subgroups);
  }, [subgroups]);
  
  const handleSave = () => {
    const mainGroupData = mainGroups.find(g => g.code === mainGroup);
    onSave(part.index, {
      main_group_code: mainGroup,
      main_group_name: mainGroupData?.name || mainGroup,
      subgroup_name: subgroupName,
      subgroup_is_new: isNew,
    });
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative w-96 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-white">Edit Classification</h4>
          <button onClick={onCancel} className="p-1 hover:bg-slate-700 rounded transition-colors">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        
        <div className="p-3 bg-slate-800/50 rounded-lg">
          <p className="text-xs text-slate-500 mb-1">Part</p>
          <p className="text-sm text-white font-medium">{part.manufacturer} {part.mpn}</p>
          <p className="text-xs text-slate-400 mt-1 line-clamp-2">{part.description}</p>
        </div>
        
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1.5">Main Group</label>
          <select
            value={mainGroup}
            onChange={(e) => {
              setMainGroup(e.target.value);
              setSubgroupName('');
              setIsNew(false);
            }}
            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {mainGroups.map(g => (
              <option key={g.code} value={g.code}>{g.code} - {g.name}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1.5">Subgroup</label>
          <select
            value={isNew ? '__new__' : subgroupName}
            onChange={(e) => {
              if (e.target.value === '__new__') {
                setIsNew(true);
                setSubgroupName('');
              } else {
                setIsNew(false);
                setSubgroupName(e.target.value);
              }
            }}
            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {localSubgroups.map(sg => (
              <option key={sg.subgroup_code} value={sg.name}>{sg.name}</option>
            ))}
            <option value="__new__">+ Create New Subgroup</option>
          </select>
          
          {isNew && (
            <input
              type="text"
              value={subgroupName}
              onChange={(e) => setSubgroupName(e.target.value)}
              placeholder="Enter new subgroup name..."
              className="mt-2 w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
            />
          )}
        </div>
        
        <div className="flex gap-2 pt-2">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!mainGroup || !subgroupName}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const Step5Complete = ({ jobData, onCopyHPN, jobId }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    className="space-y-6"
  >
    {/* Success header */}
    <div className="text-center py-6">
      <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircle2 className="w-8 h-8 text-emerald-400" />
      </div>
      <h3 className="text-xl font-semibold text-white">Import Complete!</h3>
      <p className="text-slate-400 mt-1">Successfully created {jobData?.created_count || 0} parts</p>
    </div>
    
    {/* Stats */}
    <div className="grid grid-cols-4 gap-4">
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
        <p className="text-3xl font-bold text-emerald-400">{jobData?.created_count || 0}</p>
        <p className="text-sm text-emerald-500">Parts Created</p>
      </div>
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-center">
        <p className="text-3xl font-bold text-blue-400">
          {jobData?.parts?.filter(p => p.subgroup_is_new && p.status === 'created').length || 0}
        </p>
        <p className="text-sm text-blue-500">New Subgroups</p>
      </div>
      <div className="bg-slate-800 rounded-xl p-4 text-center">
        <p className="text-3xl font-bold text-slate-300">{jobData?.duplicate_count || 0}</p>
        <p className="text-sm text-slate-500">Duplicates</p>
      </div>
      <div className="bg-slate-800 rounded-xl p-4 text-center">
        <p className="text-3xl font-bold text-slate-300">{jobData?.error_count || 0}</p>
        <p className="text-sm text-slate-500">Errors</p>
      </div>
    </div>
    
    {/* Created parts list */}
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Created Parts</h4>
        <a
          href={`/api/v1/bulk/v2/jobs/${jobId}/export`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition-colors"
        >
          <Download className="w-4 h-4" />
          Download CSV
        </a>
      </div>
      
      <div className="max-h-64 overflow-y-auto space-y-2">
        {jobData?.parts?.filter(p => p.status === 'created').map(part => (
          <div
            key={part.index}
            className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Check className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-white font-semibold">{part.existing_hpn}</span>
                  <span className="text-slate-400">•</span>
                  <span className="text-slate-300">{part.manufacturer} {part.mpn}</span>
                  {part.subgroup_is_new && (
                    <span className="px-1.5 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded">NEW SUBGROUP</span>
                  )}
                </div>
                <p className="text-sm text-slate-500">
                  {part.main_group_code} → {part.subgroup_name}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onCopyHPN(part.existing_hpn)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                title="Copy HPN"
              >
                <Copy className="w-4 h-4 text-slate-400" />
              </button>
              <a
                href={`/parts?panel=${part.existing_hpn?.replace(/\s/g, '')}`}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                title="View Part"
              >
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </a>
            </div>
          </div>
        ))}
        
        {/* Show if no parts were created */}
        {(!jobData?.parts?.filter(p => p.status === 'created').length) && (
          <div className="text-center py-4 text-slate-500">
            No parts were created
          </div>
        )}
      </div>
    </div>
    
    {/* Errors section - show if there are errors */}
    {(jobData?.error_count > 0) && (
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-red-400 uppercase tracking-wider flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Errors ({jobData?.error_count})
        </h4>
        
        <div className="max-h-48 overflow-y-auto space-y-2">
          {jobData?.parts?.filter(p => p.status === 'error').map(part => (
            <div
              key={part.index}
              className="flex items-center justify-between p-4 bg-red-500/5 rounded-xl border border-red-500/20"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <X className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white">{part.manufacturer}</span>
                    <span className="font-mono text-sm text-slate-400">{part.mpn}</span>
                  </div>
                  <p className="text-sm text-red-400 mt-1">
                    {part.error_message || 'Unknown error'}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </motion.div>
);

export default BulkImportModalV2;
